// Eskom PARCC — person-name resolution (owner R3 feedback 1).
//
// Transactions store assignee/reviewer as raw email strings; users want to see a
// real name ("Lindiwe T."). This module is the single, React-free, Firebase-free
// place that turns whatever identity data we have into a compact display name.
//
// Resolution precedence (most→least authoritative), so it degrades gracefully and
// works on TODAY's data before the new first/last fields are captured:
//   1. explicit firstName (+ lastName initial)  → "Lindiwe T."
//   2. displayName parsed (first token + last token initial)
//   3. email local part split on . _ -            → "Lindiwe T." / "Jane D."
//   4. capitalised single-token local part        → "Ltshombe" (last resort)
//   5. em-dash when there is nothing at all.
import { normalizeEmail } from './email';

export interface NameParts {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
}

const EM_DASH = '—';

function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Split an email's local part into word-ish segments (first.last, f_last, …). */
function emailSegments(email: string): string[] {
  const at = email.indexOf('@');
  const local = (at >= 0 ? email.slice(0, at) : email).trim();
  if (!local) return [];
  return local.split(/[._-]+/).filter(Boolean);
}

/** A compact display name ("Lindiwe T.") from whatever identity data exists. */
export function formatPersonName(p: NameParts): string {
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  if (first) return last ? `${first} ${last[0].toUpperCase()}.` : first;

  const dn = (p.displayName ?? '').trim();
  if (dn) {
    const parts = dn.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const surname = parts[parts.length - 1];
      return `${parts[0]} ${surname[0].toUpperCase()}.`;
    }
    return dn;
  }

  const email = (p.email ?? '').trim();
  if (email) {
    const segs = emailSegments(email);
    if (segs.length >= 2) {
      const surname = segs[segs.length - 1];
      return `${cap(segs[0])} ${surname[0].toUpperCase()}.`;
    }
    if (segs.length === 1) return cap(segs[0]);
  }
  return EM_DASH;
}

/**
 * A FULL display name ("Lindiwe Jele") from whatever identity data exists —
 * the uncompacted sibling of {@link formatPersonName}, for admin surfaces that
 * show whole names (Users editor, Notifications screen). Falls back through
 * displayName to the raw email.
 */
export function fullPersonName(p: NameParts): string {
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  const dn = (p.displayName ?? '').trim();
  if (dn) return dn;
  return (p.email ?? '').trim() || EM_DASH;
}

/** Up-to-two-letter initials for an avatar chip; "?" when nothing is available. */
export function initialsOf(p: NameParts): string {
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  if (first || last) {
    return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?';
  }
  const dn = (p.displayName ?? '').trim();
  if (dn) {
    const parts = dn.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return dn.slice(0, 2).toUpperCase();
  }
  const segs = emailSegments((p.email ?? '').trim());
  if (segs.length >= 2) return (segs[0][0] + segs[segs.length - 1][0]).toUpperCase();
  if (segs.length === 1) return segs[0].slice(0, 2).toUpperCase();
  return '?';
}

/** A row of the name directory, keyed by email downstream. */
export interface DirectoryEntry extends NameParts {
  email: string;
}

export type NameDirectory = Map<string, NameParts>;

/** Index directory rows by normalised email for O(1) assignee lookup. */
export function buildNameDirectory(entries: readonly DirectoryEntry[]): NameDirectory {
  const map: NameDirectory = new Map();
  for (const e of entries) {
    const key = normalizeEmail(e.email);
    if (key) map.set(key, e);
  }
  return map;
}

/**
 * Resolve an assignee/reviewer email to a display name via the directory, falling
 * back to an email-derived name when the person is not in the directory. Blank
 * emails render as an em-dash. The raw email is kept by callers as a `title`.
 */
export function formatAssignee(email: string | null | undefined, dir: NameDirectory): string {
  const key = normalizeEmail(email);
  if (!key) return EM_DASH;
  const entry = dir.get(key);
  return entry ? formatPersonName(entry) : formatPersonName({ email: key });
}
